import * as service from "../service";
import { store } from "../../store";
import { getAuthUserId } from "@/utils/authUser";
import type { ServiceResponse } from "../types";

export type GetMonthlyStatementParams = {
  /** 4-digit year, e.g. "2025" */
  year: string;
  /** 2-digit month, e.g. "12" */
  month: string;
};

export type MonthlyStatementRecord = {
  id?: string | number;
  date?: string;
  title?: string;
  order_id?: string | number;
  gross_amount?: number | string;
  platform_fee?: number | string;
  commission_fee?: number | string;
  net_amount?: number | string;
  [key: string]: any;
};

export type MonthlyStatementSummary = {
  total_price?: number | string;
  total_commission_fee?: number | string;
  total_original_price_used?: number | string;
  total_redeemed?: number | string;
  total_used?: number | string;
  [key: string]: any;
};

export type MonthlyStatementRange = {
  start_unix?: number;
  end_unix?: number;
  [key: string]: any;
};

export type StatementFeeLine = {
  code: string;
  amount?: number | string;
  meta?: Record<string, any>;
};

export type MonthlyStatementResult = {
  currency?: string;
  month?: string | number;
  year?: string | number;
  platform_fee_total?: number | string;
  commission_fee_total?: number | string;
  total_fee?: number | string;
  records?: MonthlyStatementRecord[];
  items?: MonthlyStatementRecord[];
  summary?: MonthlyStatementSummary;
  range?: MonthlyStatementRange;
  /** Itemized fee lines: what the business is paying for. */
  fees_breakdown?: StatementFeeLine[];
  coupon_shortfall_fee?: number | string;
  coupon_shortfall_count?: number | string;
  coupon_new_count?: number | string;
  coupon_shortfall_status?: string;
  coupon_shortfall_charged_year?: number | string | null;
  coupon_shortfall_charged_month?: number | string | null;
  /** Proof of commission payment already uploaded for this month. */
  commission_paid_proof_image?: string | null;
  user_id?: string;
  [key: string]: any;
};

/**
 * Fetch the monthly statement (platform fee + commission fee totals) of the
 * currently logged-in business user.
 * Backend: GET /api/business/monthlyStatement/getMonthlyStatement
 */
export const GetBusinessMonthlyStatementAPI = async (
  params: GetMonthlyStatementParams,
): Promise<ServiceResponse<MonthlyStatementResult>> => {
  const userId = getAuthUserId(store.getState().auth as any);
  try {
    const resp = (await service.call(
      "GET",
      "api/business/monthlyStatement/getMonthlyStatement",
      {
        user_id: userId,
        year: params.year,
        month: params.month,
      },
    )) as ServiceResponse<MonthlyStatementResult>;
    return resp;
  } catch (err) {
    console.error(`GetBusinessMonthlyStatementAPI ${err}`);
    return { status: -1, error: err };
  }
};
